import { defineRouting } from 'next-intl/routing';
import { createNavigation } from 'next-intl/navigation';
import type { ComponentType } from 'react';

export const routing = defineRouting({
  // A list of all locales that are supported
  locales: ['nl', 'de', 'en', 'fr', 'da', 'it', 'es', 'pl', 'pt', 'cs'],

  // Used when no locale matches
  defaultLocale: 'nl',

  // Always show locale prefix for clean URL structure
  // URLs: /nl (Dutch), /de (German), /en (English), /fr (French)
  localePrefix: 'always',

  // Pathname translations for each locale
  pathnames: {
    '/': '/',
    '/winkelwagen': {
      nl: '/winkelwagen',
      de: '/warenkorb',
      en: '/cart',
      da: '/cart',
      it: '/cart',
      es: '/cart',
      pl: '/cart',
      pt: '/cart',
      cs: '/cart',
      fr: '/panier'
    },
    '/favorieten': {
      nl: '/favorieten',
      de: '/favoriten',
      en: '/favorites',
      da: '/favorites',
      it: '/favorites',
      es: '/favorites',
      pl: '/favorites',
      pt: '/favorites',
      cs: '/favorites',
      fr: '/favoris'
    },
    '/account': {
      nl: '/account',
      de: '/account',
      en: '/account',
      da: '/account',
      it: '/account',
      es: '/account',
      pl: '/account',
      pt: '/account',
      cs: '/account',
      fr: '/compte'
    },
    '/account/login': {
      nl: '/account/login',
      de: '/account/login',
      en: '/account/login',
      da: '/account/login',
      it: '/account/login',
      es: '/account/login',
      pl: '/account/login',
      pt: '/account/login',
      cs: '/account/login',
      fr: '/compte/connexion'
    },
    '/account/register': {
      nl: '/account/register',
      de: '/account/register',
      en: '/account/register',
      da: '/account/register',
      it: '/account/register',
      es: '/account/register',
      pl: '/account/register',
      pt: '/account/register',
      cs: '/account/register',
      fr: '/compte/inscription'
    },
    '/account/orders': {
      nl: '/account/orders',
      de: '/account/bestellungen',
      en: '/account/orders',
      da: '/account/orders',
      it: '/account/orders',
      es: '/account/orders',
      pl: '/account/orders',
      pt: '/account/orders',
      cs: '/account/orders',
      fr: '/compte/commandes'
    },
    '/account/designs': {
      nl: '/account/designs',
      de: '/account/entwuerfe',
      en: '/account/designs',
      da: '/account/designs',
      it: '/account/designs',
      es: '/account/designs',
      pl: '/account/designs',
      pt: '/account/designs',
      cs: '/account/designs',
      fr: '/compte/designs'
    },
    '/account/settings': {
      nl: '/account/settings',
      de: '/account/einstellungen',
      en: '/account/settings',
      da: '/account/settings',
      it: '/account/settings',
      es: '/account/settings',
      pl: '/account/settings',
      pt: '/account/settings',
      cs: '/account/settings',
      fr: '/compte/parametres'
    },
    '/blog': {
      nl: '/blog',
      de: '/blog',
      en: '/blog',
      da: '/blog',
      it: '/blog',
      es: '/blog',
      pl: '/blog',
      pt: '/blog',
      cs: '/blog',
      fr: '/blog'
    },
    '/blog/[slug]': {
      nl: '/blog/[slug]',
      de: '/blog/[slug]',
      en: '/blog/[slug]',
      da: '/blog/[slug]',
      it: '/blog/[slug]',
      es: '/blog/[slug]',
      pl: '/blog/[slug]',
      pt: '/blog/[slug]',
      cs: '/blog/[slug]',
      fr: '/blog/[slug]'
    },
    '/producten': {
      nl: '/producten',
      de: '/produkte',
      en: '/products',
      da: '/products',
      it: '/products',
      es: '/products',
      pl: '/products',
      pt: '/products',
      cs: '/products',
      fr: '/produits'
    },
    '/producten/categorie/[category]': {
      nl: '/producten/categorie/[category]',
      de: '/produkte/kategorie/[category]',
      en: '/products/category/[category]',
      da: '/products/category/[category]',
      it: '/products/category/[category]',
      es: '/products/category/[category]',
      pl: '/products/category/[category]',
      pt: '/products/category/[category]',
      cs: '/products/category/[category]',
      fr: '/produits/categorie/[category]'
    },
    '/producten/[slug]': {
      nl: '/producten/[slug]',
      de: '/produkte/[slug]',
      en: '/products/[slug]',
      da: '/products/[slug]',
      it: '/products/[slug]',
      es: '/products/[slug]',
      pl: '/products/[slug]',
      pt: '/products/[slug]',
      cs: '/products/[slug]',
      fr: '/produits/[slug]'
    },
    '/templates': {
      nl: '/templates',
      de: '/templates',
      en: '/templates',
      da: '/templates',
      it: '/templates',
      es: '/templates',
      pl: '/templates',
      pt: '/templates',
      cs: '/templates',
      fr: '/templates'
    },
    '/verzending': {
      nl: '/verzending',
      de: '/versand',
      en: '/shipping',
      da: '/shipping',
      it: '/shipping',
      es: '/shipping',
      pl: '/shipping',
      pt: '/shipping',
      cs: '/shipping',
      fr: '/livraison'
    },
    '/retourneren': {
      nl: '/retourneren',
      de: '/rueckgabe',
      en: '/returns',
      da: '/returns',
      it: '/returns',
      es: '/returns',
      pl: '/returns',
      pt: '/returns',
      cs: '/returns',
      fr: '/retours'
    },
    '/over-ons': {
      nl: '/over-ons',
      de: '/uber-uns',
      en: '/about-us',
      da: '/about-us',
      it: '/about-us',
      es: '/about-us',
      pl: '/about-us',
      pt: '/about-us',
      cs: '/about-us',
      fr: '/a-propos'
    },
    '/checkout': {
      nl: '/checkout',
      de: '/kasse',
      en: '/checkout',
      da: '/checkout',
      it: '/checkout',
      es: '/checkout',
      pl: '/checkout',
      pt: '/checkout',
      cs: '/checkout',
      fr: '/paiement'
    },
    '/contact': {
      nl: '/contact',
      de: '/kontakt',
      en: '/contact',
      da: '/contact',
      it: '/contact',
      es: '/contact',
      pl: '/contact',
      pt: '/contact',
      cs: '/contact',
      fr: '/contact'
    },
    '/bestelling/[orderNumber]': {
      nl: '/bestelling/[orderNumber]',
      de: '/bestellung/[orderNumber]',
      en: '/order/[orderNumber]',
      da: '/order/[orderNumber]',
      it: '/order/[orderNumber]',
      es: '/order/[orderNumber]',
      pl: '/order/[orderNumber]',
      pt: '/order/[orderNumber]',
      cs: '/order/[orderNumber]',
      fr: '/commande/[orderNumber]'
    },
    '/bestelling/[orderNumber]/extra/[chargeId]': {
      nl: '/bestelling/[orderNumber]/extra/[chargeId]',
      de: '/bestellung/[orderNumber]/extra/[chargeId]',
      en: '/order/[orderNumber]/extra/[chargeId]',
      da: '/order/[orderNumber]/extra/[chargeId]',
      it: '/order/[orderNumber]/extra/[chargeId]',
      es: '/order/[orderNumber]/extra/[chargeId]',
      pl: '/order/[orderNumber]/extra/[chargeId]',
      pt: '/order/[orderNumber]/extra/[chargeId]',
      cs: '/order/[orderNumber]/extra/[chargeId]',
      fr: '/commande/[orderNumber]/extra/[chargeId]'
    },
    '/bestelling/[orderNumber]/bestand': {
      nl: '/bestelling/[orderNumber]/bestand',
      de: '/bestellung/[orderNumber]/datei',
      en: '/order/[orderNumber]/file',
      da: '/order/[orderNumber]/file',
      it: '/order/[orderNumber]/file',
      es: '/order/[orderNumber]/file',
      pl: '/order/[orderNumber]/file',
      pt: '/order/[orderNumber]/file',
      cs: '/order/[orderNumber]/file',
      fr: '/commande/[orderNumber]/fichier'
    },
    '/bestelling-volgen': {
      nl: '/bestelling-volgen',
      de: '/bestellung-verfolgen',
      en: '/track-order',
      da: '/track-order',
      it: '/track-order',
      es: '/track-order',
      pl: '/track-order',
      pt: '/track-order',
      cs: '/track-order',
      fr: '/suivre-commande'
    },
    '/bestandseisen': {
      nl: '/bestandseisen',
      de: '/dateianforderungen',
      en: '/file-requirements',
      da: '/file-requirements',
      it: '/file-requirements',
      es: '/file-requirements',
      pl: '/file-requirements',
      pt: '/file-requirements',
      cs: '/file-requirements',
      fr: '/exigences-fichiers'
    },
    '/kennisbank': {
      nl: '/kennisbank',
      de: '/wissensdatenbank',
      en: '/knowledge-base',
      da: '/knowledge-base',
      it: '/knowledge-base',
      es: '/knowledge-base',
      pl: '/knowledge-base',
      pt: '/knowledge-base',
      cs: '/knowledge-base',
      fr: '/base-de-connaissances'
    },
    '/kennisbank/[slug]': {
      nl: '/kennisbank/[slug]',
      de: '/wissensdatenbank/[slug]',
      en: '/knowledge-base/[slug]',
      da: '/knowledge-base/[slug]',
      it: '/knowledge-base/[slug]',
      es: '/knowledge-base/[slug]',
      pl: '/knowledge-base/[slug]',
      pt: '/knowledge-base/[slug]',
      cs: '/knowledge-base/[slug]',
      fr: '/base-de-connaissances/[slug]'
    },
    '/privacy': {
      nl: '/privacy',
      de: '/datenschutz',
      en: '/privacy',
      da: '/privacy',
      it: '/privacy',
      es: '/privacy',
      pl: '/privacy',
      pt: '/privacy',
      cs: '/privacy',
      fr: '/confidentialite'
    },
    '/algemene-voorwaarden': {
      nl: '/algemene-voorwaarden',
      de: '/agb',
      en: '/terms',
      da: '/terms',
      it: '/terms',
      es: '/terms',
      pl: '/terms',
      pt: '/terms',
      cs: '/terms',
      fr: '/conditions-generales'
    },
    '/impressum': {
      nl: '/bedrijfsgegevens',
      de: '/impressum',
      en: '/legal-notice',
      da: '/legal-notice',
      it: '/legal-notice',
      es: '/legal-notice',
      pl: '/legal-notice',
      pt: '/legal-notice',
      cs: '/legal-notice',
      fr: '/mentions-legales'
    },
    '/cookies': {
      nl: '/cookies',
      de: '/cookies',
      en: '/cookies',
      da: '/cookies',
      it: '/cookies',
      es: '/cookies',
      pl: '/cookies',
      pt: '/cookies',
      cs: '/cookies',
      fr: '/cookies'
    },
    '/betaling': {
      nl: '/betaling',
      de: '/zahlung',
      en: '/payment',
      da: '/payment',
      it: '/payment',
      es: '/payment',
      pl: '/payment',
      pt: '/payment',
      cs: '/payment',
      fr: '/paiement-info'
    },
    '/design-editor': {
      nl: '/design-editor',
      de: '/design-editor',
      en: '/design-editor',
      da: '/design-editor',
      it: '/design-editor',
      es: '/design-editor',
      pl: '/design-editor',
      pt: '/design-editor',
      cs: '/design-editor',
      fr: '/editeur-design'
    },
    '/zakelijk': {
      nl: '/zakelijk',
      de: '/geschaeftskunden',
      en: '/business',
      da: '/business',
      it: '/business',
      es: '/business',
      pl: '/business',
      pt: '/business',
      cs: '/business',
      fr: '/professionnels'
    },
    '/montage': {
      nl: '/montage',
      de: '/montage',
      en: '/installation',
      da: '/installation',
      it: '/installation',
      es: '/installation',
      pl: '/installation',
      pt: '/installation',
      cs: '/installation',
      fr: '/installation'
    },
    '/montage/bedankt': {
      nl: '/montage/bedankt',
      de: '/montage/danke',
      en: '/installation/thank-you',
      da: '/installation/thank-you',
      it: '/installation/thank-you',
      es: '/installation/thank-you',
      pl: '/installation/thank-you',
      pt: '/installation/thank-you',
      cs: '/installation/thank-you',
      fr: '/installation/merci'
    },
    '/ontwerp-service': {
      nl: '/ontwerp-service',
      de: '/design-service',
      en: '/design-service',
      da: '/design-service',
      it: '/design-service',
      es: '/design-service',
      pl: '/design-service',
      pt: '/design-service',
      cs: '/design-service',
      fr: '/service-design'
    },
    '/samenwerken': {
      nl: '/samenwerken',
      de: '/partner-werden',
      en: '/partner',
      da: '/partner',
      it: '/partner',
      es: '/partner',
      pl: '/partner',
      pt: '/partner',
      cs: '/partner',
      fr: '/partenaire'
    },
    '/stickers': {
      nl: '/stickers',
      de: '/aufkleber',
      en: '/stickers',
      da: '/stickers',
      it: '/stickers',
      es: '/stickers',
      pl: '/stickers',
      pt: '/stickers',
      cs: '/stickers',
      fr: '/autocollants'
    },
    '/foto-op-canvas': {
      nl: '/foto-op-canvas',
      de: '/foto-auf-leinwand',
      en: '/photo-on-canvas',
      da: '/photo-on-canvas',
      it: '/photo-on-canvas',
      es: '/photo-on-canvas',
      pl: '/photo-on-canvas',
      pt: '/photo-on-canvas',
      cs: '/photo-on-canvas',
      fr: '/photo-sur-toile'
    },
    '/reclameborden': {
      nl: '/reclameborden',
      de: '/werbeschilder',
      en: '/sign-boards',
      da: '/sign-boards',
      it: '/sign-boards',
      es: '/sign-boards',
      pl: '/sign-boards',
      pt: '/sign-boards',
      cs: '/sign-boards',
      fr: '/panneaux-publicitaires'
    },
    '/kerstkaarten': {
      nl: '/kerstkaarten',
      de: '/weihnachtskarten',
      en: '/christmas-cards',
      da: '/christmas-cards',
      it: '/christmas-cards',
      es: '/christmas-cards',
      pl: '/christmas-cards',
      pt: '/christmas-cards',
      cs: '/christmas-cards',
      fr: '/cartes-de-noel'
    },
    '/drukwerk': {
      nl: '/drukwerk',
      de: '/drucksachen',
      en: '/printed-matter',
      da: '/printed-matter',
      it: '/printed-matter',
      es: '/printed-matter',
      pl: '/printed-matter',
      pt: '/printed-matter',
      cs: '/printed-matter',
      fr: '/imprimes'
    },
    '/beurs-en-evenement': {
      nl: '/beurs-en-evenement',
      de: '/messe-und-event',
      en: '/trade-shows-and-events',
      da: '/trade-shows-and-events',
      it: '/trade-shows-and-events',
      es: '/trade-shows-and-events',
      pl: '/trade-shows-and-events',
      pt: '/trade-shows-and-events',
      cs: '/trade-shows-and-events',
      fr: '/salons-et-evenements'
    },
    '/inhaker-systeem': {
      nl: '/inhaker-systeem',
      de: '/inhaker-system',
      en: '/inhaker-system',
      da: '/inhaker-system',
      it: '/inhaker-system',
      es: '/inhaker-system',
      pl: '/inhaker-system',
      pt: '/inhaker-system',
      cs: '/inhaker-system',
      fr: '/systeme-inhaker'
    },
    '/bouwborden': {
      nl: '/bouwborden',
      de: '/bauschilder',
      en: '/construction-site-signs',
      da: '/construction-site-signs',
      it: '/construction-site-signs',
      es: '/construction-site-signs',
      pl: '/construction-site-signs',
      pt: '/construction-site-signs',
      cs: '/construction-site-signs',
      fr: '/panneaux-de-chantier'
    },
    '/wederverkopers': {
      nl: '/wederverkopers',
      de: '/wiederverkaeufer',
      en: '/resellers',
      da: '/resellers',
      it: '/resellers',
      es: '/resellers',
      pl: '/resellers',
      pt: '/resellers',
      cs: '/resellers',
      fr: '/revendeurs'
    },
    '/wederverkopers/handleiding': {
      nl: '/wederverkopers/handleiding',
      de: '/wiederverkaeufer/hilfe',
      en: '/resellers/help',
      da: '/resellers/help',
      it: '/resellers/help',
      es: '/resellers/help',
      pl: '/resellers/help',
      pt: '/resellers/help',
      cs: '/resellers/help',
      fr: '/revendeurs/aide'
    },
    '/wederverkopers/handleiding/[slug]': {
      nl: '/wederverkopers/handleiding/[slug]',
      de: '/wiederverkaeufer/hilfe/[slug]',
      en: '/resellers/help/[slug]',
      da: '/resellers/help/[slug]',
      it: '/resellers/help/[slug]',
      es: '/resellers/help/[slug]',
      pl: '/resellers/help/[slug]',
      pt: '/resellers/help/[slug]',
      cs: '/resellers/help/[slug]',
      fr: '/revendeurs/aide/[slug]'
    },
    '/account/reseller': {
      nl: '/account/reseller',
      de: '/account/reseller',
      en: '/account/reseller',
      da: '/account/reseller',
      it: '/account/reseller',
      es: '/account/reseller',
      pl: '/account/reseller',
      pt: '/account/reseller',
      cs: '/account/reseller',
      fr: '/compte/revendeur'
    },
    '/faq': '/faq',
    '/developers': '/developers',
    '/developers/mcp': '/developers/mcp',
    '/garantie': {
      nl: '/garantie',
      de: '/garantie',
      en: '/guarantee',
      da: '/guarantee',
      it: '/guarantee',
      es: '/guarantee',
      pl: '/guarantee',
      pt: '/guarantee',
      cs: '/guarantee',
      fr: '/garantie'
    }
  }
});

// Lightweight wrappers around Next.js' navigation APIs
// that will consider the routing configuration and pathname translations
const navigation = createNavigation(routing);

export const Link = navigation.Link as ComponentType<any>;
export const { redirect, usePathname, useRouter } = navigation;
